import React from "react";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { checkIfDataInLastFinishedWeekExists, toInstant, toZonedDateTimeOrNow } from "utils/time-utils";
import { Instant, IsoFields } from "@js-joda/core";
import { safeIdString } from "utils/utils";
import { Tooltip } from "primereact/tooltip";
import { T } from "services/lang/LanguageService";
import { useTimezone } from "hooks/useTimezone";
import getCurrentDate from "../current-date-display/CurrentDate";
import ValueContainer from "./ValueContainer";
import { getNoDataMessageWeek, getWeekYearComparison } from "./ValueChangeIndicatorUtils";
import { daysArray, formatNumber, getTextColor } from "../charts/utils";
import { DataDisplayType, DiffValueOptions, ValueChangeIndicatorProps } from "./ValueChangeIndicatorTypes";
import HorizontalBarChart from "./HorizontalBarChart";

const LastValueForWeekYearComparison = (
  props: ValueChangeIndicatorProps & {
    dataRange?: DatasetDataInfoResponse[];
    refTime: Instant;
    setCurrentDiffValueOptions: (diffValueOptions: DiffValueOptions) => void;
    setDialog: (isVisible: boolean) => void;
  }
) => {
  const tz = useTimezone();

  const noDataInLastFinishedWeekTemplate = () => {
    const lastWeek = toZonedDateTimeOrNow(tz.company, props.refTime).minusWeeks(1);
    const weekIndex = lastWeek.dayOfWeek().value() - 1;

    const weekName = daysArray[weekIndex];

    return (
      <div
        style={{ color: getTextColor(props.theme), fontWeight: "bold" }}
      >{`${T("label.no-data-in")} ${weekName}`}</div>
    );
  };

  if (
    !Array.isArray(props.dataRange) ||
    props.dataRange.length !== 1 ||
    props.dataDisplayType !== DataDisplayType.WEEK_YEAR_COMPARISON
  ) {
    return null;
  }

  const dataset = props.dataRange[0];

  if (props.useDataUpToLastFinishedMonth && dataset.data[1].key) {
    const isInCurrentMonth = checkIfDataInLastFinishedWeekExists(
      dataset.data[1].key,
      tz.company,
      props.refTime
    );
    if (!isInCurrentMonth) return noDataInLastFinishedWeekTemplate();
  }

  const comparison = getWeekYearComparison(dataset, tz.company, toInstant(props.refTime));
  if (!comparison || comparison.current == null) {
    return (
      <div style={{ color: getTextColor(props.theme) }} className="font-xl">
        {getNoDataMessageWeek(props.useDataUpToLastFinishedWeek)}
      </div>
    );
  }

  const value = comparison.current;
  const { refWeek, refYear } = comparison;

  const point = dataset.data.find(p => {
    const zdt = Instant.parse(p.key).atZone(tz.company);
    return zdt.get(IsoFields.WEEK_OF_WEEK_BASED_YEAR) === refWeek && zdt.year() === refYear;
  });

  const safeId = safeIdString(dataset.identifier);
  const tooltipId = `last-value-weekyear-${safeId}-tooltip`;

  const week = getCurrentDate({
    timezone: tz.display,
    displayWeekOfYear: true,
    year: undefined,
    month: undefined,
    day: undefined,
    refTime: props.refTime,
  });
  const year = point ? Instant.parse(point.key).atZone(tz.company).year() : refYear;
  if (props.useHorizontalBarChart) {
    if (!props.dataRange || !props.aggregationPeriod) return null;
    return (
      <div onDoubleClick={() => props.setDialog(true)}>
        <HorizontalBarChart
          data={props.dataRange[0]}
          diffType={props.diffType}
          setCurrentDiffValueOptions={props.setCurrentDiffValueOptions}
          theme={props.theme}
          reportMode={props.reportMode}
          decimalPlaces={props.decimalPlaces}
          calculateFromStartOfTheProject
          startOfTheProject={props.startOfTheProject}
          useWeekYearComparison={props.dataDisplayType === DataDisplayType.WEEK_YEAR_COMPARISON}
          refTime={props.refTime}
        />
      </div>
    );
  }

  return (
    <ValueContainer
      id={tooltipId}
      value={formatNumber(value, props.decimalPlaces, props.convertToAbsoluteValues)}
      unit={!props.hideUnit ? dataset.unit : undefined}
      highlight={props.highlightValue}
      color={getTextColor(props.theme)}
      onDoubleClick={() => props.setDialog(true)}
    >
      <Tooltip target={`#${tooltipId}`} mouseTrack mouseTrackLeft={10} position="bottom">
        {point ? (
          <div>
            {T("word.current-value")}: {value.toFixed(props.diffValueDecimalPlaces)}, {year} {week}
          </div>
        ) : (
          T("word.no-data")
        )}
      </Tooltip>
    </ValueContainer>
  );
};

export default LastValueForWeekYearComparison;
